import Link from 'next/link';
import { ARTICLE_TOKENS } from '../design-tokens';

import { useAuth0User } from '@hooks/useAuth0User';
import type { SanityCtaBannerBlock } from '@lib/article-blocks/types';
import { isPaidMember } from '@lib/utils';

const FREE_AUDIENCE = 'free';

/**
 * The page is static, so the banner is in the HTML for everyone and paid
 * members lose it once their session resolves on the client. Anonymous and
 * free readers, the audience it exists for, never see a flash.
 */
export const CtaBannerBlock = ({ value }: { value: SanityCtaBannerBlock }) => {
  const { user } = useAuth0User();
  const isHiddenForMember =
    (value.audience ?? FREE_AUDIENCE) === FREE_AUDIENCE && isPaidMember(user);
  if (isHiddenForMember || !value.heading || !value.href) return null;

  return (
    <aside
      className={`not-prose mt-11 flex flex-col gap-4 rounded-2xl px-7 py-[26px] sm:flex-row sm:items-center sm:gap-6 ${ARTICLE_TOKENS.darkShell}`}
      data-cy="cta-banner"
    >
      <div className="flex-1">
        <div className="font-heading text-[21px] font-extrabold leading-[1.2] text-white">
          {value.heading}
        </div>
        {value.text && (
          <p className="mt-1.5 mb-0 font-body text-[15px] leading-[1.45] text-[#a3abb5]">
            {value.text}
          </p>
        )}
      </div>
      <Link
        href={value.href}
        className={`inline-block whitespace-nowrap rounded-[9px] px-5 py-3 font-heading text-[14.5px] font-bold text-white no-underline ${ARTICLE_TOKENS.cta}`}
      >
        {value.buttonText ?? 'Find out more'}
      </Link>
    </aside>
  );
};
